/**
 * Calendar integration utilities for static hosting environments (GitLab Pages).
 * Generates direct Google Calendar URLs and downloadable .ics (iCalendar) files 
 * with recurring daily alarms without requiring any backend server.
 */

import { WeeklyRoutineTask, ReminderItem, ReminderOffset } from '../types';

/**
 * Converts a ReminderOffset to an iCalendar ISO 8601 duration trigger string.
 * Example: { value: 1, unit: 'week' } -> "-P1W"
 * Example: { value: 1, unit: 'hour' } -> "-PT1H"
 */
export function offsetToIcsTrigger(offset: ReminderOffset): string {
  const { value, unit } = offset;
  if (value === 0) return '-PT0M';

  switch (unit) {
    case 'minutes':
      return `-PT${value}M`;
    case 'hours':
      return `-PT${value}H`;
    case 'days':
      return `-P${value}D`;
    case 'weeks':
      return `-P${value}W`;
    default:
      return `-PT${value}M`;
  }
}

/**
 * Parses an iCalendar TRIGGER string (e.g., "-PT1H", "-P1W", "-PT15M", "-P3D") back into a ReminderOffset rule.
 */
export function parseIcsTriggerToOffset(triggerStr: string): ReminderOffset | null {
  if (!triggerStr) return null;
  const clean = triggerStr.trim().toUpperCase();

  if (clean === '-PT0M' || clean === '-PT0S' || clean === 'PT0M') {
    return { value: 0, unit: 'minutes' };
  }

  // Weeks match: -P1W, -P2W
  const weekMatch = clean.match(/-?P(\d+)W/);
  if (weekMatch) return { value: parseInt(weekMatch[1], 10), unit: 'weeks' };

  // Days match: -P1D, -P3D
  const dayMatch = clean.match(/-?P(\d+)D/);
  if (dayMatch) return { value: parseInt(dayMatch[1], 10), unit: 'days' };

  // Hours match: -PT1H, -PT2H
  const hourMatch = clean.match(/-?PT(\d+)H/);
  if (hourMatch) return { value: parseInt(hourMatch[1], 10), unit: 'hours' };

  // Minutes match: -PT15M, -PT30M, -PT5M
  const minMatch = clean.match(/-?PT(\d+)M/);
  if (minMatch) return { value: parseInt(minMatch[1], 10), unit: 'minutes' };

  return null;
}

/**
 * Helper to generate VALARM blocks for a reminder item based on its relative offsets.
 */
function generateValarmLines(reminder: ReminderItem): string[] {
  const offsets = reminder.reminders && reminder.reminders.length > 0
    ? reminder.reminders
    : [{ value: 15, unit: 'minutes' as const }];

  const lines: string[] = [];
  offsets.forEach((offset) => {
    const trigger = offsetToIcsTrigger(offset);
    lines.push(
      'BEGIN:VALARM',
      `TRIGGER:${trigger}`,
      'ACTION:DISPLAY',
      `DESCRIPTION:یادآوری: ${reminder.title}`,
      'END:VALARM'
    );
  });
  return lines;
}

export interface CalendarReminderItem {
  id: string;
  title: string;
  time: string; // HH:mm format, e.g. "08:30"
  enabled: boolean;
  description?: string;
}

const TITLE_TRANSLATIONS: Record<string, string> = {
  'Rehab Reminder': 'تمرینات فیزیوتراپی و ریکاوری کمر (برنامه پیشرفت)',
  'Fat Loss Workout': 'فعالیت هوازی و چربی‌سوزی (برنامه پیشرفت)',
  'Water Hydration': 'نوشیدن آب و هیدراتاسیون (برنامه پیشرفت)',
  'Swedish Study Session': 'مطالعه روزانه زبان سوئدی (برنامه پیشرفت)',
  'Work Checklist Prep': 'بررسی چک‌لیست کاری و آماده‌سازی فردا (برنامه پیشرفت)',
};

const DESCRIPTION_TRANSLATIONS: Record<string, string> = {
  'Rehab Reminder': 'زمان انجام حرکات کششی، دکومپرشن کمر و آزادسازی پسواس جهت پیشگیری از دیسک کمر.',
  'Fat Loss Workout': 'زمان پیاده‌روی، تمرین هوازی و فعالیت فیزیکی روزانه جهت سلامت قلب و سوزاندن چربی.',
  'Water Hydration': 'یادآوری نوشیدن یک لیوان آب جهت حفظ هیدراتاسیون بدن و شادابی عضلات.',
  'Swedish Study Session': 'زمان مطالعه لغات، گرامر و تمرین مکالمه زبان سوئدی SFI.',
  'Work Checklist Prep': 'زمان بررسی لیست کارهای انجام شده امروز و برنامه‌ریزی اولویت‌های کاری فردا.',
};

export function getPersianTitle(title: string): string {
  return TITLE_TRANSLATIONS[title] || title;
}

export function getPersianDescription(title: string): string {
  return DESCRIPTION_TRANSLATIONS[title] || 'یادآور روزانه برنامه خودمراقبتی و مدیریت سلامت پیشرفت';
}

/**
 * Creates an ISO date string for Google Calendar in UTC format: YYYYMMDDTHHmmSSZ
 */
function formatToGCalDateTime(date: Date): string {
  return date.toISOString().replace(/-|:|\.\d+/g, '');
}

/**
 * Generates a direct Google Calendar Web Link that opens Google Calendar 
 * with pre-filled title, details, daily recurrence, and reminder time.
 */
export function createGoogleCalendarUrl(
  title: string, 
  timeStr: string, 
  customDetails?: string,
  durationMinutes = 15
): string {
  const persianTitle = getPersianTitle(title);
  const details = customDetails || getPersianDescription(title);

  const [hours, minutes] = timeStr.split(':').map(Number);
  
  // Set start time for today or tomorrow
  const startDate = new Date();
  startDate.setHours(hours || 8, minutes || 0, 0, 0);

  // If time has already passed today, start from tomorrow
  if (startDate.getTime() < Date.now()) {
    startDate.setDate(startDate.getDate() + 1);
  }

  const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);

  const startIso = formatToGCalDateTime(startDate);
  const endIso = formatToGCalDateTime(endDate);

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: persianTitle,
    details: `${details}\n\n📱 ثبت شده از طریق برنامه خودمراقبتی پیشرفت (Progress App)`,
    dates: `${startIso}/${endIso}`,
    recur: 'RRULE:FREQ=DAILY', // Daily repeating reminder
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generates standard RFC 5545 .ics (iCalendar) text for a single reminder or multiple reminders
 * complete with daily recurrence rules and alarms for native phone calendar apps.
 */
export function generateIcsContent(reminders: CalendarReminderItem[]): string {
  const activeReminders = reminders.filter(r => r.enabled);
  if (activeReminders.length === 0) return '';

  const nowIso = formatToGCalDateTime(new Date());

  let icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Progress Wellness App//Persian Self-Care Calendar//FA',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:یادآورهای روزانه پیشرفت (Progress Reminders)',
    'X-WR-TIMEZONE:Asia/Tehran'
  ];

  activeReminders.forEach((reminder) => {
    const persianTitle = getPersianTitle(reminder.title);
    const details = reminder.description || getPersianDescription(reminder.title);
    
    const [hours, minutes] = (reminder.time || '08:00').split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    if (startDate.getTime() < Date.now()) {
      startDate.setDate(startDate.getDate() + 1);
    }
    const endDate = new Date(startDate.getTime() + 15 * 60 * 1000);

    const dtStart = formatToGCalDateTime(startDate);
    const dtEnd = formatToGCalDateTime(endDate);

    icsLines.push(
      'BEGIN:VEVENT',
      `UID:progress-reminder-${reminder.id}-${startDate.getTime()}@progress.app`,
      `DTSTAMP:${nowIso}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      'RRULE:FREQ=DAILY', // Repeat every single day
      `SUMMARY:${persianTitle}`,
      `DESCRIPTION:${details}`,
      'STATUS:CONFIRMED',
      // Add native phone alarm trigger 0 minutes before event
      'BEGIN:VALARM',
      'TRIGGER:-PT0M',
      'ACTION:DISPLAY',
      `DESCRIPTION:زمان انجام ${persianTitle}`,
      'END:VALARM',
      'END:VEVENT'
    );
  });

  icsLines.push('END:VCALENDAR');

  return icsLines.join('\r\n');
}

const DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

/**
 * Generates standard RFC 5545 .ics (iCalendar) text for weekly schedule tasks.
 * Each task is configured with a weekly recurrence rule for its specific day of week,
 * complete with native phone alarms.
 */
export function generateWeeklyRoutineIcsContent(weeklyRoutine: WeeklyRoutineTask[]): string {
  if (!weeklyRoutine || weeklyRoutine.length === 0) return '';

  // Filter ONLY tasks that have Google Calendar / Calendar Reminder enabled
  const enabledTasks = weeklyRoutine.filter(t => t.reminderEnabled);
  if (enabledTasks.length === 0) return '';

  const now = new Date();
  const nowIso = formatToGCalDateTime(now);

  let icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Progress Wellness App//Persian Weekly Schedule Calendar//FA',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:برنامه‌ریزی و روتین‌های هفتگی پیشرفت (Progress Weekly Schedule)',
    'X-WR-TIMEZONE:Asia/Tehran'
  ];

  enabledTasks.forEach((task) => {
    const title = task.title || 'فعالیت روتین هفتگی';
    const details = task.description || 'ثبت شده در برنامه‌ریزی هفتگی پیشرفت (Progress App)';
    
    const targetDayNum = Number(task.dayOfWeek); // 0 = Sun, 1 = Mon, ..., 6 = Sat
    const dayCode = DAY_CODES[targetDayNum] || 'MO';

    // Calculate next occurrence date for dtStart
    const currentDayNum = now.getDay();
    let dayDiff = targetDayNum - currentDayNum;
    if (dayDiff < 0) dayDiff += 7;

    const startDate = new Date(now);
    startDate.setDate(now.getDate() + dayDiff);

    const [hours, minutes] = (task.time || '08:00').split(':').map(Number);
    startDate.setHours(hours || 8, minutes || 0, 0, 0);

    // End date calculation
    const endDate = new Date(startDate);
    if (task.endTime) {
      const [endH, endM] = task.endTime.split(':').map(Number);
      endDate.setHours(endH || hours + 1, endM || 0, 0, 0);
    } else {
      endDate.setMinutes(startDate.getMinutes() + 30);
    }

    const dtStart = formatToGCalDateTime(startDate);
    const dtEnd = formatToGCalDateTime(endDate);

    icsLines.push(
      'BEGIN:VEVENT',
      `UID:weekly-routine-${task.id}-${startDate.getTime()}@progress.app`,
      `DTSTAMP:${nowIso}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      `RRULE:FREQ=WEEKLY;BYDAY=${dayCode}`, // Recur weekly on specified day
      `SUMMARY:${title}`,
      `DESCRIPTION:${details}`,
      'STATUS:CONFIRMED',
      'BEGIN:VALARM',
      'TRIGGER:-PT0M', // Trigger alarm at exact start time on phone
      'ACTION:DISPLAY',
      `DESCRIPTION:زمان انجام ${title}`,
      'END:VALARM',
      'END:VEVENT'
    );
  });

  icsLines.push('END:VCALENDAR');

  return icsLines.join('\r\n');
}

/**
 * Downloads an .ics calendar file directly in the browser.
 */
export function downloadIcsFile(filename: string, icsContent: string): void {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Creates a direct Google Calendar URL for a family reminder item.
 */
export function createReminderGoogleCalendarUrl(reminder: ReminderItem): string {
  const [year, month, day] = (reminder.date || new Date().toISOString().split('T')[0]).split('-').map(Number);
  const [hours, minutes] = (reminder.time || '10:00').split(':').map(Number);
  
  const startDate = new Date(year, month - 1, day, hours, minutes, 0);
  const endDate = new Date(startDate.getTime() + 30 * 60 * 1000); // 30 min event
  
  const startIso = formatToGCalDateTime(startDate);
  const endIso = formatToGCalDateTime(endDate);

  const categoryNames: Record<string, string> = {
    birthday: 'تولد و مناسبت‌های خاص',
    event: 'رویداد و قرار ملاقات',
    bills: 'قبوض و پرداخت‌های مالی',
    school: 'مدرسه و کلاس آموزشی',
    medical: 'پزشکی و سلامت',
    shopping: 'خرید و مرسولات',
    personal: 'امور شخصی و اداری',
    family: 'مدیریت خانواده'
  };

  const details = `${reminder.description || reminder.title}\n\n👤 مربوط به: ${reminder.person}\n📂 دسته‌بندی: ${categoryNames[reminder.category] || reminder.category}\n📱 ثبت شده در مرکز یادآورهای خانوادگی پیشرفت`;

  let recur = '';
  if (reminder.repeat && reminder.repeat !== 'none') {
    if (reminder.repeat === 'daily') recur = 'RRULE:FREQ=DAILY';
    else if (reminder.repeat === 'weekly') recur = 'RRULE:FREQ=WEEKLY';
    else if (reminder.repeat === 'monthly') recur = 'RRULE:FREQ=MONTHLY';
    else if (reminder.repeat === 'yearly') recur = 'RRULE:FREQ=YEARLY';
  }

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `📌 ${reminder.title} (${reminder.person})`,
    details: details,
    dates: `${startIso}/${endIso}`,
  });

  if (recur) {
    params.set('recur', recur);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generates standard RFC 5545 .ics (iCalendar) text for family reminders.
 */
export function generateFamilyRemindersIcsContent(reminders: ReminderItem[]): string {
  const activeReminders = (reminders || []).filter(r => r.status !== 'completed');
  if (activeReminders.length === 0) return '';

  const nowIso = formatToGCalDateTime(new Date());

  let icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Progress Wellness App//Family Reminders Calendar//FA',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:مرکز یادآورهای خانوادگی پیشرفت (Family Reminders)',
    'X-WR-TIMEZONE:Asia/Tehran'
  ];

  activeReminders.forEach((reminder) => {
    const [year, month, day] = (reminder.date || new Date().toISOString().split('T')[0]).split('-').map(Number);
    const [hours, minutes] = (reminder.time || '10:00').split(':').map(Number);

    const startDate = new Date(year, month - 1, day, hours, minutes, 0);
    const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);

    const dtStart = formatToGCalDateTime(startDate);
    const dtEnd = formatToGCalDateTime(endDate);

    const details = `${reminder.description || ''}\\n👤 مربوط به: ${reminder.person}\\n📱 ثبت شده از مرکز یادآورهای خانوادگی پیشرفت`;

    const rrule = reminder.repeat && reminder.repeat !== 'none'
      ? `RRULE:FREQ=${reminder.repeat.toUpperCase()}`
      : null;

    const valarms = generateValarmLines(reminder);

    icsLines.push(
      'BEGIN:VEVENT',
      `UID:family-reminder-${reminder.id}-${startDate.getTime()}@progress.app`,
      `DTSTAMP:${nowIso}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      ...(rrule ? [rrule] : []),
      `SUMMARY:📌 ${reminder.title} (${reminder.person})`,
      `DESCRIPTION:${details}`,
      'STATUS:CONFIRMED',
      ...valarms,
      'END:VEVENT'
    );
  });

  icsLines.push('END:VCALENDAR');
  return icsLines.join('\r\n');
}

/**
 * Generates combined .ics calendar containing both Weekly Routine tasks and Family Reminders.
 */
export function generateCombinedIcsContent(weeklyRoutine: WeeklyRoutineTask[], reminders: ReminderItem[]): string {
  const enabledTasks = (weeklyRoutine || []).filter(t => t.reminderEnabled);
  const activeReminders = (reminders || []).filter(r => r.status !== 'completed');

  if (enabledTasks.length === 0 && activeReminders.length === 0) return '';

  const now = new Date();
  const nowIso = formatToGCalDateTime(now);

  let icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Progress Wellness App//Full Calendar Export//FA',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:تقویم جامع برنامه هفتگی و یادآورهای خانوادگی پیشرفت',
    'X-WR-TIMEZONE:Asia/Tehran'
  ];

  // 1. Weekly Routine Tasks
  enabledTasks.forEach((task) => {
    const title = task.title || 'فعالیت روتین هفتگی';
    const details = task.description || 'ثبت شده در برنامه‌ریزی هفتگی پیشرفت (Progress App)';
    
    const targetDayNum = Number(task.dayOfWeek);
    const dayCode = DAY_CODES[targetDayNum] || 'MO';

    const currentDayNum = now.getDay();
    let dayDiff = targetDayNum - currentDayNum;
    if (dayDiff < 0) dayDiff += 7;

    const startDate = new Date(now);
    startDate.setDate(now.getDate() + dayDiff);

    const [hours, minutes] = (task.time || '08:00').split(':').map(Number);
    startDate.setHours(hours || 8, minutes || 0, 0, 0);

    const endDate = new Date(startDate);
    if (task.endTime) {
      const [endH, endM] = task.endTime.split(':').map(Number);
      endDate.setHours(endH || hours + 1, endM || 0, 0, 0);
    } else {
      endDate.setMinutes(startDate.getMinutes() + 30);
    }

    const dtStart = formatToGCalDateTime(startDate);
    const dtEnd = formatToGCalDateTime(endDate);

    icsLines.push(
      'BEGIN:VEVENT',
      `UID:weekly-routine-${task.id}-${startDate.getTime()}@progress.app`,
      `DTSTAMP:${nowIso}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      `RRULE:FREQ=WEEKLY;BYDAY=${dayCode}`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${details}`,
      'STATUS:CONFIRMED',
      'BEGIN:VALARM',
      'TRIGGER:-PT0M',
      'ACTION:DISPLAY',
      `DESCRIPTION:زمان انجام ${title}`,
      'END:VALARM',
      'END:VEVENT'
    );
  });

  // 2. Family Reminders
  activeReminders.forEach((reminder) => {
    const [year, month, day] = (reminder.date || new Date().toISOString().split('T')[0]).split('-').map(Number);
    const [hours, minutes] = (reminder.time || '10:00').split(':').map(Number);

    const startDate = new Date(year, month - 1, day, hours, minutes, 0);
    const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);

    const dtStart = formatToGCalDateTime(startDate);
    const dtEnd = formatToGCalDateTime(endDate);

    const details = `${reminder.description || ''}\\n👤 مربوط به: ${reminder.person}\\n📱 ثبت شده از مرکز یادآورهای خانوادگی پیشرفت`;

    const rrule = reminder.repeat && reminder.repeat !== 'none'
      ? `RRULE:FREQ=${reminder.repeat.toUpperCase()}`
      : null;

    const valarms = generateValarmLines(reminder);

    icsLines.push(
      'BEGIN:VEVENT',
      `UID:family-reminder-${reminder.id}-${startDate.getTime()}@progress.app`,
      `DTSTAMP:${nowIso}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      ...(rrule ? [rrule] : []),
      `SUMMARY:📌 ${reminder.title} (${reminder.person})`,
      `DESCRIPTION:${details}`,
      'STATUS:CONFIRMED',
      ...valarms,
      'END:VEVENT'
    );
  });

  icsLines.push('END:VCALENDAR');
  return icsLines.join('\r\n');
}

/**
 * Parses an imported .ics calendar file into reminder objects with their VALARM reminder offsets preserved.
 */
export function parseIcsToReminders(icsContent: string): Partial<ReminderItem>[] {
  if (!icsContent || typeof icsContent !== 'string') return [];

  const results: Partial<ReminderItem>[] = [];
  const veventBlocks = icsContent.split(/BEGIN:VEVENT/i).slice(1);

  veventBlocks.forEach((block) => {
    const endIdx = block.indexOf('END:VEVENT');
    const content = endIdx !== -1 ? block.slice(0, endIdx) : block;

    // Parse SUMMARY / Title
    let title = 'یادآور وارد شده';
    const summaryMatch = content.match(/SUMMARY:(.*?)(\r?\n|$)/i);
    if (summaryMatch && summaryMatch[1]) {
      title = summaryMatch[1].replace(/^📌\s*/, '').replace(/\s*\([^)]*\)$/, '').trim();
    }

    // Parse DTSTART -> Date and Time
    let dateStr = new Date().toISOString().split('T')[0];
    let timeStr = '09:00';

    const dtstartMatch = content.match(/DTSTART(?:;[^:]*)?:(\d{8})(?:T(\d{4,6}))?/i);
    if (dtstartMatch) {
      const d = dtstartMatch[1];
      dateStr = `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
      if (dtstartMatch[2]) {
        const t = dtstartMatch[2];
        timeStr = `${t.slice(0, 2)}:${t.slice(2, 4)}`;
      }
    }

    // Parse RRULE
    let repeat: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly' = 'none';
    const rruleMatch = content.match(/RRULE:.*FREQ=(DAILY|WEEKLY|MONTHLY|YEARLY)/i);
    if (rruleMatch && rruleMatch[1]) {
      repeat = rruleMatch[1].toLowerCase() as any;
    }

    // Parse Description
    let description = '';
    const descMatch = content.match(/DESCRIPTION:(.*?)(\r?\n[A-Z-]+:|$)/is);
    if (descMatch && descMatch[1]) {
      description = descMatch[1].replace(/\\n/g, '\n').trim();
    }

    // Parse VALARM triggers
    const offsets: ReminderOffset[] = [];
    const triggerMatches = content.matchAll(/TRIGGER:(.*?)(\r?\n|$)/gi);
    for (const tm of triggerMatches) {
      if (tm[1]) {
        const parsedOffset = parseIcsTriggerToOffset(tm[1]);
        if (parsedOffset) {
          const exists = offsets.some(o => o.value === parsedOffset.value && o.unit === parsedOffset.unit);
          if (!exists) {
            offsets.push(parsedOffset);
          }
        }
      }
    }

    results.push({
      title: title || 'یادآور بدون عنوان',
      date: dateStr,
      time: timeStr,
      repeat,
      description,
      category: 'personal',
      person: 'من',
      priority: 'medium',
      status: 'pending',
      reminders: offsets.length > 0 ? offsets : [{ value: 1, unit: 'hours' }]
    });
  });

  return results;
}
